import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthorizationService } from '@service/authorization.service';

export type HelpAudience = 'anwender' | 'kis-partner' | 'softwarepartner';

/**
 * Which roles may read which help section.
 * Must match `Roles.Help*` in `Administration.Web/Authorization/Role.cs` (the backend enforces it).
 */
export const HELP_AUDIENCE_ROLES: Record<HelpAudience, string[]> = {
  anwender: ['RodixAdmin', 'User', 'SuperUserKomplett', 'SuperUserMandant'],
  'kis-partner': ['RodixAdmin', 'KIS-partner', 'SuperUserKIS'],
  softwarepartner: ['RodixAdmin', 'Softwarepartner'],
};

export const canReadHelp = (authorizationService: AuthorizationService, audience: HelpAudience): boolean => {
  return authorizationService.hasOneOfTheRoles(HELP_AUDIENCE_ROLES[audience]);
};

/** Protects a help route; `route.data.audience` names the section. */
export const helpAudienceGuard: CanActivateFn = (route) => {
  const audience = route.data['audience'] as HelpAudience;
  return canReadHelp(inject(AuthorizationService), audience) || inject(Router).createUrlTree(['/hilfe']);
};

export const isExternalLink = (href: string): boolean => {
  return /^[a-z][a-z\d+.-]*:/i.test(href) || href.startsWith('//');
};

/**
 * Resolves a link inside a Markdown page relative to that page, like a browser would.
 * `('anwender/index.md', 'files/Handbuch%20A.pdf')` → `'anwender/files/Handbuch A.pdf'`
 */
export const resolveHelpPath = (pagePath: string, href: string): string => {
  const url = new URL(href, `https://help.invalid/${pagePath}`);
  return url.pathname.slice(1).split('/').map(decodeURIComponent).join('/');
};
